import React from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import MobileChildSessionItem from './MobileChildSessionItem';
import { formatDisplayDate } from '../../utils/timeUtils';

const MobileSessionCard = ({
  session,
  crews,
  canModify,
  onToggleExpand,
  isExpanded,
  handleEdit,
  handleSchedule,
  handleDelete,
  handleViewDetails,
  handleReschedule,
  handleQuickSchedule,
  formatCrewNames,
  formatSessionTiming,
  getSessionStatus,
}) => {
  const hasChildren = session.childSessions && session.childSessions.length > 0;
  const sessionStatus = getSessionStatus(session);
  const isParentSession = !session.parent_session_id && hasChildren;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Main Session Card */}
      <div className="space-y-3 bg-white p-4">
        {/* Title and Status Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasChildren && (
              <button
                onClick={() => onToggleExpand(session.id)}
                className="text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            )}
            <button
              className="truncate text-left text-base font-semibold text-gray-900 transition-colors hover:text-blue-600"
              onClick={() => handleViewDetails(session)}
              title="View session details"
            >
              {session.title}
            </button>
            {hasChildren && (
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                {session.childSessions.length}
              </span>
            )}
          </div>
          <span
            className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              sessionStatus === 'completed'
                ? 'bg-green-100 text-green-800'
                : sessionStatus === 'partially-scheduled'
                  ? 'bg-blue-100 text-blue-800'
                  : sessionStatus === 'scheduled'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {sessionStatus === 'completed'
              ? 'Done'
              : sessionStatus === 'partially-scheduled'
                ? 'Partial'
                : sessionStatus === 'scheduled'
                  ? 'Scheduled'
                  : 'Unscheduled'}
          </span>
        </div>

        {/* Description Row */}
        {session.description && <div className="text-sm text-gray-600">{session.description}</div>}

        {/* Date Row */}
        <div className="flex items-center text-sm text-gray-600">
          <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
          <span>{formatDisplayDate(new Date(session.date))}</span>
        </div>

        {/* Time Row */}
        <div className="flex items-center text-sm text-gray-600">
          <ClockIcon className="mr-2 h-4 w-4 text-gray-400" />
          <span>{formatSessionTiming(session)}</span>
        </div>

        {/* Crews Row */}
        <div className="text-sm text-gray-600">
          <div className="mb-1 flex items-center">
            <UsersIcon className="mr-2 h-4 w-4 text-gray-400" />
            <span className="font-medium">Crews:</span>
            {session.crew_assignments && session.crew_assignments.length > 0 && (
              <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800">
                {session.crew_assignments.length}
              </span>
            )}
          </div>
          <div className="ml-6">
            {!session.crew_assignments || session.crew_assignments.length === 0 ? (
              <span className="text-gray-500">
                {session && session.childSessions && session.childSessions.length > 0
                  ? 'All crews scheduled'
                  : 'Open Session'}
              </span>
            ) : (
              session.crew_assignments.map((assignment, index) => {
                const crew = crews.find((crew) => crew.id === assignment.crew_id);
                return (
                  <div key={index} className="text-gray-700">
                    {crew?.name || 'Unknown Crew'}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Location Row */}
        {session.location && (
          <div className="flex items-center text-sm text-gray-600">
            <svg
              className="mr-2 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="truncate">{session.location}</span>
          </div>
        )}

        {/* Actions Row */}
        {canModify && (
          <div className="flex gap-2 border-t border-gray-100 pt-2">
            {/* Regular session actions */}
            {(session.session_type === 'unscheduled' ||
              (isParentSession && sessionStatus !== 'completed')) && (
              <>
                <button
                  onClick={() => handleSchedule(session)}
                  className="flex items-center rounded-md bg-blue-100 px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-200"
                >
                  <CalendarIcon className="mr-1 h-3 w-3" />
                  Schedule
                </button>
                <button
                  onClick={() => handleQuickSchedule(session)}
                  className="flex items-center rounded-md bg-green-100 px-3 py-1.5 text-xs text-green-700 hover:bg-green-200"
                >
                  <ClockIcon className="mr-1 h-3 w-3" />
                  Quick
                </button>
              </>
            )}

            <button
              onClick={() => handleEdit(session)}
              className="flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-200"
            >
              <PencilIcon className="mr-1 h-3 w-3" />
              Edit
            </button>

            <button
              onClick={() => handleDelete(session)}
              className="flex items-center rounded-md bg-red-100 px-3 py-1.5 text-xs text-red-700 hover:bg-red-200"
            >
              <TrashIcon className="mr-1 h-3 w-3" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Child Sessions */}
      {hasChildren && isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          {session.childSessions.map((childSession) => (
            <MobileChildSessionItem
              key={childSession.id}
              childSession={childSession}
              crews={crews}
              canModify={canModify}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleReschedule={handleReschedule}
              handleViewDetails={handleViewDetails}
              formatSessionTiming={formatSessionTiming}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileSessionCard;
